import COOKIE_CONSENT from '../../../conf/CookiesConsentConfig.json'
import { CookieConsent } from '../../../i18n'
import { COOKIE_CONSENT_BUTTON_CLASS, INLINE_BUTTON_CLASS } from '../constants/class-names'
import { SERVLET_COMMAND, ServletCmds } from '../constants/ServletCommands'
import { createButton, createDiv } from '../DOM/DOMUtils'
import CLanguageHandler from '../Languages/LanguageHandler'
import {
    addCookieConsent,
    deleteAllUnacceptedCookies,
    getCookieConsent,
    hasCookieConsent,
    hasCookieConsentSetting,
    removeCookieConsent,
    setCookieConsent
} from '../utils/CookieConsentUtils'
import { IsVarEmpty } from '../utils/GlobalFunctions'
import { $window, getDocumentBody } from '../utils/WindowFunctions'
import { grantGACookieConsent } from './GoogleAnalytics'

const getButtonHookElement = (): Nullable<HTMLDivElement> =>
    document.querySelector<HTMLDivElement>('div[data-button-hook]')

const getButtonHook = (): Promise<Nullable<HTMLDivElement>> =>
    new Promise<Nullable<HTMLDivElement>>((resolve: (value: Nullable<HTMLDivElement>) => void): void => {
        const buttonHook: Nullable<HTMLDivElement> = getButtonHookElement()

        if (buttonHook) {
            resolve(buttonHook)
        } else {
            $window.addEventListener('load', (): void => resolve(getButtonHookElement()))
        }
    })

const initaliseButtonHook = (): void => {
    getButtonHook().then((buttonHook: Nullable<HTMLDivElement>): void => {
        if (buttonHook) {
            buttonHook.innerHTML = ''
            // eslint-disable-next-line
            createCookieConsentButtons(buttonHook)
        }
    })
}

export const rejectAllCookies =
    (rejectionWarning: string): (() => void) =>
    (): void => {
        // eslint-disable-next-line
        alert(rejectionWarning)
        setCookieConsent([])
        deleteAllUnacceptedCookies()
    }

export const acceptFunctional = (): void => {
    addCookieConsent(...COOKIE_CONSENT.functional)
    removeCookieConsent(...COOKIE_CONSENT.statistics)
    deleteAllUnacceptedCookies()
}

export const acceptAll = (): void => {
    addCookieConsent(...COOKIE_CONSENT.functional)
    grantGACookieConsent()
}

const createCookieConsentButtons = (container: HTMLElement, removeOnSelection?: HTMLElement): void => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(CookieConsent.NAME)

    const hasAcceptedFunctional: boolean = hasCookieConsent(COOKIE_CONSENT.functional[0])
    const hasAcceptedAll: boolean = hasAcceptedFunctional && hasCookieConsent(COOKIE_CONSENT.statistics[0])

    // Button container.
    const buttonContainer: HTMLElement = createDiv('ccb-button-container')

    container.appendChild(buttonContainer)

    if (IsVarEmpty(removeOnSelection)) {
        const getMessage = (): Nullable<string> => {
            if (hasAcceptedAll) {
                return language.getInterpolated(
                    CookieConsent.AcceptedAllCookiesMessage,
                    language.get(CookieConsent.CookieConsentChangeMessage)
                )
            }

            if (hasAcceptedFunctional) {
                return language.getInterpolated(
                    CookieConsent.AcceptedFunctionalCookiesMessage,
                    language.get(CookieConsent.CookieConsentChangeMessage)
                )
            }

            if (hasCookieConsentSetting()) {
                return language.getInterpolated(
                    CookieConsent.RejectedAllCookiesMessage,
                    language.get(CookieConsent.CookieConsentChangeMessage)
                )
            }

            return null
        }

        // Create message.
        const message: Nullable<string> = getMessage()

        if (message) {
            const messageElement: HTMLElement = createDiv({
                classes: ['ccb-message'],
                content: message
            })

            buttonContainer.appendChild(messageElement)
        }
    }

    // Create the cookie consent banner buttons. This will be added to the cookie consent banner.
    const onClickFactory =
        (...funcs: (() => void)[]): (() => void) =>
        (): void => {
            if (funcs) {
                funcs.forEach((func: () => void): void => func())
            }

            removeOnSelection?.remove()
            initaliseButtonHook()
        }

    if (hasAcceptedAll || hasAcceptedFunctional || removeOnSelection || !hasCookieConsentSetting()) {
        const rejectAllButton: HTMLButtonElement = createButton({
            content:
                hasAcceptedAll || hasAcceptedFunctional
                    ? language.get(CookieConsent.RevokeAllCookies)
                    : language.get(CookieConsent.RejectAllCookies),
            classes: [INLINE_BUTTON_CLASS, COOKIE_CONSENT_BUTTON_CLASS],
            onClick: onClickFactory(rejectAllCookies(language.get(CookieConsent.RejectionWarning)))
        })

        buttonContainer.appendChild(rejectAllButton)
    }

    if (!hasAcceptedFunctional || hasAcceptedAll) {
        const acceptFunctionalityButton: HTMLButtonElement = createButton({
            content: hasAcceptedAll
                ? language.get(CookieConsent.RevokeStatisticsCookies)
                : language.get(CookieConsent.AcceptFunctionalityCookies),
            classes: [INLINE_BUTTON_CLASS, COOKIE_CONSENT_BUTTON_CLASS],
            onClick: onClickFactory(acceptFunctional)
        })

        buttonContainer.appendChild(acceptFunctionalityButton)
    }

    if (!hasAcceptedAll) {
        const acceptAllButton: HTMLButtonElement = createButton({
            content: language.get(CookieConsent.AcceptAllCookies),
            classes: [INLINE_BUTTON_CLASS, COOKIE_CONSENT_BUTTON_CLASS],
            onClick: onClickFactory(acceptAll)
        })

        buttonContainer.appendChild(acceptAllButton)
    }
}

export const initialiseCookieConsentBanner = (): void => {
    const hasCookieBannerNotInitialised: boolean = IsVarEmpty(
        document.querySelector('.cookie-consent-banner-container')
    )

    if (hasCookieBannerNotInitialised) {
        const consentNotGiven: boolean = IsVarEmpty(getCookieConsent())
        const queryParams: URLSearchParams = new URLSearchParams($window?.location?.search)

        if (
            consentNotGiven &&
            queryParams.get(SERVLET_COMMAND) !== ServletCmds.COOKIE_POLICY &&
            !queryParams.get('CookiePolicyStop')
        ) {
            const language: CLanguageHandler = CLanguageHandler.getInstance()
            language.loadBundle(CookieConsent.NAME)

            // Create the cookie consent banner container. This will be added to the DOM Body.
            const cookieConsentBannerContainer: HTMLDivElement = createDiv('cookie-consent-banner-container')

            // Create the cookie consent banner. This will be added to the cookie consent banner container.
            const cookieConsentBanner: HTMLDivElement = createDiv('cookie-consent-banner')

            cookieConsentBannerContainer.appendChild(cookieConsentBanner)

            // Create the cookie consent banner columns. This will be added to the cookie consent banner.
            const ccbLeft: HTMLDivElement = createDiv({
                classes: ['ccb-left'],
                content: language.getInterpolated(
                    CookieConsent.CookiesDescription,
                    ` <a href="${$window.sSERVLETPATH}?ServletCmd=CMD_COOKIEPOLICY">`,
                    '</a>'
                )
            })
            const ccbRight: HTMLDivElement = createDiv('ccb-right')

            cookieConsentBanner.appendChild(ccbLeft)
            cookieConsentBanner.appendChild(ccbRight)

            createCookieConsentButtons(ccbRight, cookieConsentBannerContainer)

            // Add the cookie consent banner container to the DOM Body.
            getDocumentBody().then(
                (body: HTMLBodyElement): HTMLDivElement => body.appendChild(cookieConsentBannerContainer)
            )
        }
    }
}

setTimeout((): void => {
    initialiseCookieConsentBanner()
    initaliseButtonHook()
})
